export type SystemRole =
  | 'admin'
  | 'executive'
  | 'product_manager'
  | 'team_lead'
  | 'engineer'
  | 'designer'
  | 'sales'
  | 'marketing'
  | 'viewer';

export type EntityRole = 'admin' | 'member';

export type FunctionalRole =
  | 'executive'
  | 'product_manager'
  | 'team_lead'
  | 'engineer'
  | 'designer'
  | 'sales'
  | 'marketing';

export type ProjectStatus =
  | 'draft'
  | 'planning'
  | 'active'
  | 'on_track'
  | 'at_risk'
  | 'complete'
  | 'archived';

export type TicketStatus = 'triage' | 'at_risk' | 'on_track' | 'planning' | 'complete' | 'idea' | 'invalid';

export type TicketType = 'feature' | 'enhancement' | 'bug_fix' | 'task';

export interface EntityUser {
  userId: string;
  email: string;
  fullName: string;
  role?: SystemRole;
  entityRole?: EntityRole;
  functionalRoles: FunctionalRole[];
  primaryRole?: FunctionalRole | null;
  department: string | null;
  profilePictureUrl: string | null;
  // Single source of truth for avatar precedence: 'manual' (user-uploaded in
  // Settings) always wins over provider-derived images (Google, ...). Absent
  // for legacy docs written before this field existed — treat as non-manual.
  avatarSource?: 'manual' | 'google' | null;
  alertPreferences: { email: boolean; inApp: boolean; slack: boolean };
  isActive: boolean;
  lastSeenAt: string | null;
  createdAt: string;
  onboardingCompletedAt?: string | null;
}

export interface ProjectLink {
  linkId: string;
  projectId: string;
  entityId: string;
  provider: Integration['provider'];
  // Discriminates the resource shape behind a link — needed once a single
  // provider ('google') can mean a Drive folder or a Drive file (or,
  // elsewhere, a calendar link). Optional for older links written before
  // this field existed.
  resourceType?: 'jira_project' | 'linear_team' | 'slack_channel' | 'drive_folder' | 'drive_file' | string;
  externalResourceId: string;
  displayName: string;
  webUrl: string | null;
  syncEnabled: boolean;
  autoLinked?: boolean;
  // System-set health signal, distinct from syncEnabled (the user's own
  // on/off intent) — see backend app/workers/sync.py's _sync_google.
  linkHealth?: 'ok' | 'error' | 'unknown';
  linkHealthCheckedAt?: string | null;
  createdAt: string;
}

export interface IntegrationResource {
  id: string;
  name: string;
  type: string;
  url?: string;
  extra?: Record<string, string>;
}

export interface Project {
  projectId: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  ownerId: string | null;
  ownerName: string | null;
  overallRiskScore: number | null;
  riskCacheIsStale: boolean;
  riskScoreSeries?: number[];
  /** Canonical risk trend ('worsening' | 'improving' | 'stable' | 'unknown'),
   * written by risk.py alongside riskScoreSeries. Absent on projects not yet
   * re-evaluated — treat as unknown, never as stable. */
  riskTrend?: string | null;
  targetLaunchDate?: string | null;
  morningBriefEnabled?: boolean;
  briefDetailLevel?: 'summary' | 'detailed';
  isParkingLot?: boolean;
  linkedKrIds?: LinkedKr[];
  teamLeadId?: string | null;
  visibility?: 'team' | 'company' | null;
  executiveAccess?: 'all' | 'selected' | null;
  executiveIds?: string[] | null;
  createdAt: string;
  updatedAt: string;
  links?: ProjectLink[];
}

export interface ProjectMember {
  involvementId: string;
  projectId: string;
  entityId: string;
  userId: string;
  role: 'owner' | 'contributor' | 'suggested';
  addedBy?: string;
  addedAt: string;
}

export interface TicketArtifact {
  type: 'jira' | 'slack' | 'google_drive' | 'url' | 'linear';
  url: string;
  label: string | null;
  externalId: string | null;
}

/**
 * One ingested provider comment. Canonical storage shape written by
 * backend/app/workers/sync.py (and the demo seeder). Tickets synced before
 * 2026-08-15 may still carry the legacy joined-string form — always read
 * `recentComments` through lib/comments.ts `normalizeComments`.
 */
export interface TicketComment {
  author: string | null;
  text: string;
  createdAt?: string | null;
}

export interface Ticket {
  suggestedActions?: SuggestedAction[];
  actionsGeneratedAt?: string | null;
  completions?: Record<string, ActionCompletion>;
  ticketId: string;
  projectId: string;
  projectName: string;
  title: string;
  description: string | null;
  status: TicketStatus;
  type: TicketType;
  assigneeId: string | null;
  assigneeName: string | null;
  artifacts: TicketArtifact[];
  aiSummary: string | null;
  summaryIsStale: boolean;
  dueDate?: string | null;
  priority?: number | null;
  estimate?: number | null;
  recentComments?: TicketComment[] | string | null;
  labels?: string[] | null;
  priorityLabel?: string | null;
  sourceCreatedAt?: string | null;
  sourceUpdatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RiskFacet {
  name: 'Communication' | 'Schedule' | 'Definition' | 'Scope' | 'Dependency' | 'Deadline';
  score: number;
  reasoning: string;
  signals: string[];
}

export interface RiskCache {
  projectId: string;
  overallScore: number;
  facets: RiskFacet[];
  confidence?: number;
  deliveryConfidence?: number;
  trend?: string;
  isStale: boolean;
  generatedAt: string;
  projectActions?: SuggestedAction[];
  actionsGeneratedAt?: string | null;
  completions?: Record<string, ActionCompletion>;
}

export type ActionType =
  | 'slack_message'
  | 'slack_dm'
  | 'create_doc'
  | 'create_slides'
  | 'create_calendar_event'
  | 'post_ticket_comment'
  | 'update_ticket_status'
  | 'bulk_update_ticket_status'
  | 'status_report'
  | 'open_url';

export interface SuggestedAction {
  actionId: string;
  type: ActionType;
  label: string;
  description: string;
  roles: string[];
  priority: 'high' | 'medium' | 'low';
  payload: Record<string, unknown>;
}

export interface ActionResult {
  outcome: 'sent' | 'url' | 'content';
  message: string;
  url?: string;
  content?: string;
  /**
   * Ground truth for whether the side effect actually happened, distinct
   * from `outcome` — a handler can return outcome "url" both for a real
   * created doc/slide/event AND a disconnected-integration fallback link.
   * See backend/app/routers/actions.py's prerequisite fix (Release 4).
   */
  didExecute?: boolean;
  /**
   * Set when this response did NOT dispatch a fresh side effect — a second
   * caller hit an actionId already claimed (state "done" or
   * "needs-your-action") and was handed back the existing completion
   * record instead of re-triggering it. See the claim-then-execute
   * transaction in execute_action.
   */
  alreadyCompleted?: boolean;
  completion?: ActionCompletion;
}

/**
 * Server-authoritative completion record, stored as a sibling
 * `completions[actionId]` map on the SAME cache doc the action itself
 * lives on (insightCache/latest, a project's riskCache/latest, or a
 * ticket doc's `suggestedActions`) — see backend Release 4. This is the
 * source of truth for "is this action done" across page reloads and
 * across users; the client Zustand actionStore is session-local optimistic
 * state only, used for the fresh in-modal result view right after
 * executing.
 */
export type CompletionState = 'pending' | 'done' | 'needs-your-action' | 'failed';

export interface ActionCompletion {
  userId: string;
  userName: string;
  userEmail: string;
  state: CompletionState;
  message: string;
  completedAt: string | null;
  /**
   * TTL hint only — the `generatedAt`/`actionsGeneratedAt` of the source
   * cache doc at claim time. Not something the client checks; regeneration
   * mints fresh actionIds so a stale completion simply stops matching
   * anything once the source doc regenerates.
   */
  sourceGeneratedAt?: string | null;
}

export interface InsightAction {
  actionId?: string;
  label: string;
  projectId: string;
  projectName: string;
  type: string;
  // Optional enriched fields (present when generated with new action format)
  payload?: Record<string, unknown>;
  roles?: string[];
  priority?: 'high' | 'medium' | 'low';
  description?: string;
}

export interface InsightSummaryItem {
  projectId: string;
  text: string;
}

export interface DailyInsight {
  summary: string | InsightSummaryItem[];  // string = legacy cache, array = current format
  actions: InsightAction[];
  isStale: boolean;
  generatedAt: string;
  completions?: Record<string, ActionCompletion>;
}

export type BriefingVoice = 'acting' | 'recommending' | 'observational';

export interface BriefingArtifact {
  channel?: string;
  message?: string;
  title?: string;
  bullets?: string[];
  content?: string;
  description?: string;
  attendeeEmails?: string[];
  durationMinutes?: number;
}

export interface BriefingItem {
  id: string;
  projectId: string;
  projectName: string;
  status: string;
  voice: BriefingVoice;
  headline: string;
  why: string[];
  artifactKind: 'slack' | 'plan' | 'observation' | 'calendar' | 'doc';
  artifact: BriefingArtifact | null;
  actions: {
    approve: string;
    edit: string;
    skip: string;
  };
  actionType: ActionType;
  actionPayload: Record<string, unknown>;
  roles: string[];
}

export interface DailyBriefing {
  // No "greeting" field: the server used to send one, but a value baked in
  // at generation time and shared by every colleague viewing this per-role
  // document can't reflect either "who's looking" or "what time is it for
  // them" reliably. The greeting is composed client-side instead — see
  // TodayView.tsx's getTimeOfDay() — from the viewing user's own identity
  // and local clock, the same source the "N things · this evening" tagline
  // already used.
  headline: string;
  items: BriefingItem[];
  resting: {
    onTrack: number;
    complete: number;
    total: number;
  };
  generatedAt: string;
  isStale: boolean;
  /**
   * The persona this document was actually generated/resolved for, echoed
   * back by GET /briefing/today. The server validates the requested role
   * against the caller's own functionalRoles and silently substitutes a
   * role the caller actually holds when the client's guess doesn't match
   * (e.g. a stale activeRole selection) — this field is how the client
   * finds out, so it can key its cache and any role-driven chrome off the
   * truth instead of its own guess. See useTodayBriefing().
   */
  role: FunctionalRole;
}

/**
 * Response body of POST /briefing/today/refresh. `cooldown: true` means the
 * entity+role cooldown window (backend: _REFRESH_COOLDOWN) was still in
 * effect and no generation ran — the caller must distinguish this from a
 * normal in-flight refresh, or the control reads as dead. See
 * useRefreshBriefing() / TodayView.
 */
export interface BriefingRefreshResult {
  queued: boolean;
  cooldown: boolean;
  retryAfterSeconds?: number;
  entityId: string;
  targetRole: string;
}

export interface SessionConfig {
  userId: string;
  entityId: string;
  role?: SystemRole;
  entityRole?: EntityRole;
  functionalRoles: FunctionalRole[];
  entityName: string;
  orgLogoUrl: string | null;
  testingMode: boolean;
  /**
   * Server-resolved gate for the four "maybe later" persona dashboards
   * (designer, team_lead, engineer, sales) — see
   * backend/app/routers/users.py get_session_config and
   * frontend/src/lib/personas.ts. False (the default when a backend
   * predating this field is in play) means those dashboards are gated off.
   */
  legacyPersonasEnabled?: boolean;
  /**
   * The entity's configured risk alert threshold (RiskConfig.alertThreshold
   * — range 2.0-4.5, default 3.0; see backend/app/routers/users.py
   * get_session_config and RiskConfiguration.tsx). Surfaces that band a
   * risk score (frontend/src/lib/riskBand.ts) should pass this through
   * rather than relying on the utility's DEFAULT_ALERT_THRESHOLD fallback,
   * so the UI agrees with server-rendered email/Slack bands (both derived
   * from backend/app/utils/risk_bands.py using this same threshold) for
   * entities that customized it. Undefined (a backend predating this field,
   * or session not yet loaded) means callers should fall back to the
   * shared default.
   */
  alertThreshold?: number;
}

export type SyncSchedule = '30m' | '1h' | '2h' | '4h' | '24h' | 'paused';
export type RiskSchedule = 'nightly' | 'weekly' | 'paused';

export interface Integration {
  provider: 'slack' | 'jira' | 'google' | 'linear';
  jiraType?: 'cloud' | 'datacenter';
  isConnected: boolean;
  connectedByUserId: string | null;
  connectedAt: string | null;
  syncSchedule: SyncSchedule;
  metadata: {
    workspaceName?: string;
    teamId?: string;
    siteName?: string;
    siteUrl?: string;
    accountEmail?: string;
    // Jira Data Center fields
    instanceUrl?: string;
    instanceName?: string;
    email?: string;
    dcVersion?: string;
  };
  syncStatus: 'idle' | 'syncing' | 'error';
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  // Computed fresh on every read (see backend/app/routers/integrations.py) —
  // never written by a scheduled job, so its own detection can't silently
  // stop working the way a missed sync itself could. True when a connected,
  // non-paused integration hasn't synced in well beyond its configured
  // interval.
  syncOverdue?: boolean;
}

export interface PersonalIntegration {
  provider: 'google';
  isConnected: boolean;
  connectedAt: string | null;
  updatedAt: string | null;
  metadata: {
    accountEmail?: string;
    displayName?: string;
    pictureUrl?: string;
    scopes?: string[];
  };
  status: 'pending' | 'connected' | 'disconnected';
  // True when the account's granted Google scopes include something broader
  // than what OctoPM currently requests (an old connection made before the
  // 2026-08 scope narrowing) — surfaces the re-consent prompt.
  needsReconsent?: boolean;
  // entities/{entityId}/users/{userId}/integrations/google : visibilityLevel
  // "personal" (writes only, no ingestion) | "project_signals" (default) | "full".
  visibilityLevel?: 'personal' | 'project_signals' | 'full';
}

export interface Invite {
  inviteId: string;
  email: string;
  role?: SystemRole;
  entityRole?: EntityRole;
  functionalRoles?: FunctionalRole[];
  entityId: string;
  entityName: string;
  invitedByUserId: string;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
}

export interface FacetWeights {
  communication: number;
  schedule: number;
  definition: number;
  scope: number;
  dependency: number;
  deadline: number;
}

export interface RiskConfig {
  alertThreshold: number;
  facetWeights: FacetWeights;
  staleTicketDays: number;
  minTicketsForScore: number;
  includedTicketTypes: string[];
}

export const DEFAULT_FACET_WEIGHTS: FacetWeights = {
  communication: 1.0,
  schedule: 1.0,
  definition: 1.0,
  scope: 1.0,
  dependency: 1.0,
  deadline: 1.0,
};

export const DEFAULT_RISK_CONFIG: RiskConfig = {
  alertThreshold: 3.0,
  facetWeights: DEFAULT_FACET_WEIGHTS,
  staleTicketDays: 5,
  minTicketsForScore: 3,
  includedTicketTypes: ['feature', 'enhancement', 'bug_fix', 'task'],
};

export type KrStatus = 'not_started' | 'in_progress' | 'at_risk' | 'achieved';
export type GoalStatus = 'active' | 'achieved' | 'cancelled';

export interface KeyResult {
  krId: string;
  title: string;
  metric: string | null;
  progress: number;
  status: KrStatus;
}

export interface Goal {
  goalId: string;
  title: string;
  description: string | null;
  timeframe: string | null;
  ownerId: string | null;
  ownerName: string | null;
  status: GoalStatus;
  keyResults: KeyResult[];
  createdAt: string;
  updatedAt: string;
}

export interface LinkedKr {
  goalId: string;
  krId: string;
}

export interface AiKeyConfig {
  useByok: boolean;
  keyLast4: string | null;
  configuredAt: string | null;
  validatedAt: string | null;
  status: 'ok' | 'error' | null;
  lastError: string | null;
  lastErrorAt: string | null;
}

export interface CompanySettings {
  entityName: string;
  logoUrl: string | null;
  timezone: string;
  aiRefreshSchedule: string;
  riskSchedule?: RiskSchedule;
  riskConfig?: RiskConfig;
  aiKeyConfig?: AiKeyConfig;
}

export interface StatusUpdateRequest {
  format?: 'slack' | 'email' | 'doc';
  includeRisk?: boolean;
  includePreviousUpdate?: boolean;
}
